import React, { useEffect, useRef } from 'react';

const STEPS = 160;
const GOLDEN_ANGLE = 2.399963229728653;
// Equal shares of cool blue-white and warm champagne particles.
const PARTICLE_TONES = [0, 1];
const PARTICLE_COLORS = [
  ['244, 248, 255', '194, 211, 238', '120, 149, 191'],
  ['252, 248, 238', '237, 224, 191', '186, 163, 121'],
];

type Strand = {
  points: Float32Array;
  paletteOffset: number;
  phase: number;
  speed: number;
  opacity: number;
  highlight: boolean;
};

type Scene = { opacity: number };
type SceneAnchor = Scene & { top: number };

const SCENES = [
  {
    id: 'Header', opacity: 1,
  },
  {
    id: 'AI', opacity: 0.32,
  },
  {
    id: 'HardSkills', opacity: 0.38,
  },
  {
    id: 'Process', opacity: 0.36,
  },
  {
    id: 'Collaboration', opacity: 0.32,
  },
  {
    id: 'Contacts', opacity: 0.48,
  },
];

function cubic(t: number, a: number, b: number, c: number, d: number) {
  const remaining = 1 - t;
  return remaining ** 3 * a + 3 * remaining ** 2 * t * b + 3 * remaining * t ** 2 * c + t ** 3 * d;
}

function getProjection(width: number, height: number) {
  // Crop the distant tail in portrait viewports, including tablets.
  const portrait = height > width;
  return {
    focal: Math.max(width * 0.9, height * (portrait ? 1.4 : 0.82)),
    centerX: width * (portrait ? 0.31 : 0.56),
    centerY: height * (portrait ? 0.49 : 0.53),
  };
}

function createStrands(width: number, height: number): Strand[] {
  const count = width < 600 ? 96 : 112;
  const { focal, centerX, centerY } = getProjection(width, height);
  return Array.from({ length: count }, (_, index) => {
    const theta = index * GOLDEN_ANGLE;
    const radius = Math.sqrt((index + 0.5) / count);
    const across = Math.cos(theta) * radius;
    const depth = Math.sin(theta) * radius;
    const seed = Math.sin(index * 127.1 + 19.7) * 43758.5453;
    const variation = seed - Math.floor(seed);
    const points = new Float32Array((STEPS + 1) * 2);

    // A two-dimensional cross-section travels through depth toward the lens.
    // There is no shared exit curve or lower canvas edge for the strands.
    for (let step = 0; step <= STEPS; step += 1) {
      const t = step / STEPS;
      const perspective = focal / (2.6 - 2.53 * t);
      points[step * 2] = centerX + (cubic(t, 0.85, 0.55, -0.12, 0.08)
        + across * (0.035 + 0.39 * t ** 3)) * perspective;
      points[step * 2 + 1] = centerY + (cubic(t, -1.15, -0.55, 0.03, 0.04)
        + depth * (0.018 + 0.19 * t ** 3)) * perspective;
    }
    return {
      points,
      paletteOffset: index % PARTICLE_TONES.length,
      phase: variation,
      speed: 0.018 + variation * 0.010,
      opacity: 0.24 + variation * 0.36,
      highlight: index % 6 === 0,
    };
  });
}

const LightFlow: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);
  const strandsRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const sceneElement = sceneRef.current;
    const strandsCanvas = strandsRef.current;
    const strandsContext = strandsCanvas?.getContext('2d');
    const canvas = particlesRef.current;
    const context = canvas?.getContext('2d');
    if (!container || !sceneElement || !strandsCanvas || !strandsContext || !canvas || !context) {
      return undefined;
    }

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const touchInput = window.matchMedia('(pointer: coarse)');
    const sprites = PARTICLE_COLORS.map(([core, glow, halo]) => {
      const sprite = document.createElement('canvas');
      sprite.width = 64;
      sprite.height = 64;
      const spriteContext = sprite.getContext('2d');
      if (spriteContext) {
        const gradient = spriteContext.createRadialGradient(32, 32, 0, 32, 32, 32);
        gradient.addColorStop(0, `rgba(${core}, 1)`);
        gradient.addColorStop(0.08, `rgba(${glow}, 0.95)`);
        gradient.addColorStop(0.22, `rgba(${halo}, 0.42)`);
        gradient.addColorStop(1, `rgba(${halo}, 0)`);
        spriteContext.fillStyle = gradient;
        spriteContext.fillRect(0, 0, 64, 64);
      }
      return sprite;
    });

    let width = 0;
    let height = 0;
    let strands: Strand[] = [];
    let anchors: SceneAnchor[] = [];
    let frame: number | null = null;
    let previousFrame: number | null = null;
    let pendingDelta = 0;
    let frameInterval = 1000 / 60;
    let pixelRatio = 0;
    let elapsed = 0;
    let disposed = false;
    let scrollPosition = window.scrollY;
    let maxScroll = 1;
    let scrimScrollEnd = 1;
    let cameraZoom = 1;
    let lastTransform = '';
    let lastOpacity = '';
    const scene: Scene = { ...SCENES[0] };

    const setOverlayOpacity = (property: string, value: number) => {
      const next = value.toFixed(4);
      if (container.style.getPropertyValue(property) !== next) {
        container.style.setProperty(property, next);
      }
    };

    const updateScrim = () => {
      const progress = Math.max(0, Math.min(1, window.scrollY / scrimScrollEnd));
      const opacity = progress * progress * (3 - 2 * progress);
      setOverlayOpacity('--flow-scrim-opacity', opacity);
      const heroWeight = Math.max(0, 1 - scrollPosition / Math.max(1, anchors[1]?.top || height));
      setOverlayOpacity('--flow-hero-mask-opacity', heroWeight);
    };

    const measureScenes = () => {
      maxScroll = Math.max(1, document.documentElement.scrollHeight - height);
      anchors = SCENES.flatMap((item, index) => {
        const element = document.getElementById(item.id);
        if (!element) {
          return [];
        }
        return [{
          ...item,
          top: index === 0 ? 0 : Math.max(0, element.getBoundingClientRect().top + window.scrollY - height * 0.28),
        }];
      });
      const firstSection = document.getElementById('AI');
      const headerHeight = document.querySelector('.site-header')?.getBoundingClientRect().height || 0;
      scrimScrollEnd = Math.max(1, firstSection
        ? firstSection.getBoundingClientRect().top + window.scrollY - headerHeight
        : height);
      updateScrim();
    };

    const updateScene = (delta: number, immediate: boolean) => {
      const follow = immediate ? 1 : 1 - Math.exp(-delta / 850);
      const scrollProgress = Math.max(0, Math.min(1, scrollPosition / maxScroll));
      const targetZoom = reducedMotion.matches ? 1 : 1 + scrollProgress * 0.12;
      cameraZoom += (targetZoom - cameraZoom) * follow;
      const transform = `scale(${cameraZoom.toFixed(5)})`;
      if (lastTransform !== transform) {
        sceneElement.style.transform = transform;
        lastTransform = transform;
      }
      if (!anchors.length) {
        return;
      }
      let current = anchors[0];
      let next = current;
      for (let index = 1; index < anchors.length; index += 1) {
        next = anchors[index];
        if (scrollPosition < next.top) {
          break;
        }
        current = next;
      }
      const progress = Math.max(0, Math.min(1, (scrollPosition - current.top) / Math.max(1, next.top - current.top)));
      const ease = progress * progress * (3 - 2 * progress);
      const targetOpacity = current.opacity + (next.opacity - current.opacity) * ease;
      scene.opacity += (targetOpacity - scene.opacity) * follow;
      const opacity = scene.opacity.toFixed(4);
      if (lastOpacity !== opacity) {
        sceneElement.style.opacity = opacity;
        lastOpacity = opacity;
      }
    };

    // Rebuild this layer only on resize. Camera changes transform both layers together.
    const renderStrands = () => {
      strandsContext.clearRect(0, 0, width, height);
      strandsContext.globalCompositeOperation = 'lighter';
      strandsContext.lineCap = 'round';
      strandsContext.lineJoin = 'round';
      strands.forEach((strand) => {
        const { points } = strand;
        strandsContext.beginPath();
        strandsContext.moveTo(points[0], points[1]);
        for (let step = 1; step <= STEPS; step += 1) {
          strandsContext.lineTo(points[step * 2], points[step * 2 + 1]);
        }
        const alpha = strand.opacity * 0.65;
        if (strand.highlight) {
          strandsContext.strokeStyle = `rgba(100, 103, 207, ${alpha * 0.12})`;
          strandsContext.lineWidth = 6.5;
          strandsContext.stroke();
        }
        strandsContext.strokeStyle = strand.highlight
          ? `rgba(178, 176, 238, ${alpha})`
          : `rgba(109, 102, 182, ${alpha})`;
        strandsContext.lineWidth = strand.highlight ? 1.05 : 0.75;
        strandsContext.stroke();
      });
    };

    const render = (delta = 16.67, immediate = false) => {
      if (!width || !height) {
        return;
      }
      updateScene(delta, immediate);
      const seconds = elapsed / 1000;
      context.clearRect(0, 0, width, height);
      context.globalCompositeOperation = 'lighter';

      strands.forEach((strand) => {
        const { points } = strand;
        for (let pulse = 0; pulse < 3; pulse += 1) {
          const progress = (strand.phase + seconds * strand.speed + pulse / 3) % 1;
          const position = progress * STEPS;
          const point = Math.floor(position);
          const fraction = position - point;
          const offset = point * 2;
          const x = points[offset] + (points[offset + 2] - points[offset]) * fraction;
          const y = points[offset + 1] + (points[offset + 3] - points[offset + 1]) * fraction;
          const perspective = 1 / (2.6 - progress * 2.53);
          const size = Math.min(56, (strand.highlight ? 10 : 6.5) * perspective);
          const fade = Math.min(1, progress * 8, (1 - progress) * 11);
          context.globalAlpha = fade * (strand.highlight ? 0.95 : 0.7);
          if (x > -size && x < width + size && y > -size && y < height + size) {
            const tone = PARTICLE_TONES[(strand.paletteOffset + pulse) % PARTICLE_TONES.length];
            context.drawImage(sprites[tone], x - size / 2, y - size / 2, size, size);
          }
        }
        context.globalAlpha = 1;
      });

      context.globalCompositeOperation = 'source-over';
    };

    const animate = (timestamp: number) => {
      frame = null;
      if (disposed || document.hidden || reducedMotion.matches) {
        return;
      }
      const delta = previousFrame === null ? frameInterval : Math.min(timestamp - previousFrame, 100);
      previousFrame = timestamp;
      elapsed += delta;
      pendingDelta += delta;
      if (pendingDelta + 0.01 >= frameInterval) {
        const renderDelta = Math.floor((pendingDelta + 0.01) / frameInterval) * frameInterval;
        render(renderDelta);
        pendingDelta = Math.max(0, pendingDelta - renderDelta);
      }
      frame = window.requestAnimationFrame(animate);
    };

    const updateAnimation = () => {
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      frame = null;
      previousFrame = null;
      pendingDelta = 0;
      if (disposed) {
        return;
      }
      if (reducedMotion.matches) {
        elapsed = 0;
        render(16.67, true);
      } else if (!document.hidden) {
        frame = window.requestAnimationFrame(animate);
      }
    };

    const resize = () => {
      const nextWidth = container.clientWidth;
      const nextHeight = container.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      if (!nextWidth || !nextHeight || (width === nextWidth && height === nextHeight && pixelRatio === dpr)) {
        return;
      }
      width = nextWidth;
      height = nextHeight;
      pixelRatio = dpr;
      frameInterval = 1000 / (width < 600 || touchInput.matches ? 30 : 60);
      const { centerX, centerY } = getProjection(width, height);
      sceneElement.style.transformOrigin = `${centerX}px ${centerY}px`;
      container.style.setProperty('--flow-mask-radius', `${width * 0.49}px`);
      strandsCanvas.width = Math.round(width * dpr);
      strandsCanvas.height = Math.round(height * dpr);
      canvas.width = strandsCanvas.width;
      canvas.height = strandsCanvas.height;
      strandsContext.setTransform(dpr, 0, 0, dpr, 0, 0);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      strands = createStrands(width, height);
      renderStrands();
      measureScenes();
      render(16.67, true);
    };

    const scroll = () => {
      scrollPosition = window.scrollY;
      updateScrim();
      if (reducedMotion.matches) {
        render(16.67, true);
      }
    };

    const resizeObserver = new ResizeObserver(resize);
    const layoutObserver = new ResizeObserver(measureScenes);
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-flow-visible', 'true');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.06, rootMargin: '0px 0px -24px 0px' });
    const reveals = document.querySelectorAll('[data-flow-reveal]');
    reveals.forEach((element, index) => {
      element.setAttribute('data-flow-side', index % 2 ? 'right' : 'left');
      revealObserver.observe(element);
    });
    const layout = container.parentElement;
    layout?.setAttribute('data-flow-ready', 'true');
    const main = document.getElementById('content');
    if (main) {
      layoutObserver.observe(main);
    }
    resizeObserver.observe(container);
    window.addEventListener('scroll', scroll, { passive: true });
    document.addEventListener('visibilitychange', updateAnimation);
    reducedMotion.addEventListener('change', updateAnimation);
    resize();
    updateAnimation();

    return () => {
      disposed = true;
      if (frame !== null) {
        window.cancelAnimationFrame(frame);
      }
      resizeObserver.disconnect();
      layoutObserver.disconnect();
      revealObserver.disconnect();
      layout?.removeAttribute('data-flow-ready');
      container.style.removeProperty('--flow-scrim-opacity');
      container.style.removeProperty('--flow-hero-mask-opacity');
      container.style.removeProperty('--flow-mask-radius');
      reveals.forEach((element) => {
        element.removeAttribute('data-flow-visible');
        element.removeAttribute('data-flow-side');
      });
      window.removeEventListener('scroll', scroll);
      document.removeEventListener('visibilitychange', updateAnimation);
      reducedMotion.removeEventListener('change', updateAnimation);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      className="site-light-flow"
    >
      <div
        ref={sceneRef}
        className="light-flow-scene"
      >
        <canvas
          ref={strandsRef}
          className="light-flow-strands"
        />
        <canvas
          ref={particlesRef}
          className="light-flow-particles"
        />
      </div>
    </div>
  );
};

export default LightFlow;
